// React hooks for the admin Authors page. Hits /admin/authors when the
// VITE_ARTICLES_API flag is on; otherwise hydrates from the mock
// AUTHORS + AUTHOR_STATS tables so the page renders in offline dev.

import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { articlesApiEnabled } from "./articlesApi";
import { AUTHORS, AUTHOR_STATS, type Author, type AuthorKey } from "./admin-mock";

export type EditorialRole = "contributor" | "editor";

// Wire row from GET /admin/authors.
export interface AuthorWire {
  id: string;
  email: string;
  name: string;
  agency: string;
  cea_number: string;
  editorial_role: EditorialRole;
  status: string;
  published: number;
  drafts: number;
  in_review: number;
  total_leads: number;
  last_publish_at?: string;
}

// Page-level shape — derives display fields off the wire row. Pages
// continue using `Author` from admin-mock for tint/initials lookup,
// so we expose both halves and let the renderer compose them.
export interface AuthorEntry {
  id: string;
  wire: AuthorWire;
  display: Author;
  // Stat block matches AUTHOR_STATS shape so the existing card layout
  // can drop in unchanged.
  stats: {
    drafts: number;
    awaiting: number;
    published: number;
    leads: number;
    views: number;
    last: string;
  };
}

const TINT_POOL = ["#1d4a3a", "#6b5320", "#2a4a66", "#7a2d1f", "#3a2e7a"];

function pickTint(id: string): string {
  let sum = 0;
  for (const c of id) sum = (sum + c.charCodeAt(0)) % TINT_POOL.length;
  return TINT_POOL[sum];
}

function makeInitials(name: string, email: string): string {
  const src = name.trim() || email.split("@")[0];
  const parts = src.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function adaptAuthor(w: AuthorWire): AuthorEntry {
  const initials = makeInitials(w.name, w.email);
  const team = w.editorial_role === "editor" ? "Editorial" : "Contributor";
  return {
    id: w.id,
    wire: w,
    display: {
      name: w.name || w.email,
      initials,
      tint: pickTint(w.id),
      team,
    },
    stats: {
      drafts: w.drafts,
      awaiting: w.in_review,
      published: w.published,
      leads: Number(w.total_leads ?? 0),
      // views isn't yet aggregated per author; surface 0 rather than
      // fabricating. The card layout treats 0 sensibly.
      views: 0,
      last: w.last_publish_at ? relTime(w.last_publish_at) : "—",
    },
  };
}

function relTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)}d ago`;
  return d.toLocaleDateString("en-SG", { month: "short", day: "numeric" });
}

// Mock fallback: synthesise AuthorEntry rows from AUTHORS + AUTHOR_STATS
// so the page renders identical visuals without a backend.
function mockEntries(): AuthorEntry[] {
  return (Object.entries(AUTHORS) as Array<[AuthorKey, Author]>).map(([id, a]) => {
    const s = AUTHOR_STATS.find((x) => x.id === id);
    return {
      id,
      // Wire is a best-effort projection — only used for the toggle
      // button to fall through to the real PATCH in live mode.
      wire: {
        id,
        email: `${id}@propwise.example.com`,
        name: a.name,
        agency: "",
        cea_number: "",
        editorial_role: a.team === "Editorial" ? "editor" : "contributor",
        status: "active",
        published: s?.published ?? 0,
        drafts: s?.drafts ?? 0,
        in_review: s?.awaiting ?? 0,
        total_leads: s?.leads ?? 0,
      },
      display: a,
      stats: {
        drafts: s?.drafts ?? 0,
        awaiting: s?.awaiting ?? 0,
        published: s?.published ?? 0,
        leads: s?.leads ?? 0,
        views: s?.views ?? 0,
        last: s?.last ?? "—",
      },
    };
  });
}

export function useAuthors(): {
  authors: AuthorEntry[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [authors, setAuthors] = useState<AuthorEntry[]>(() =>
    articlesApiEnabled() ? [] : mockEntries(),
  );
  const [loading, setLoading] = useState(articlesApiEnabled());
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!articlesApiEnabled()) {
      setAuthors(mockEntries());
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<{ authors: AuthorWire[]; total: number }>(`/admin/authors?limit=200`)
      .then((resp) => {
        if (cancelled) return;
        setAuthors(resp.authors.map(adaptAuthor));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { authors, loading, error, refresh: () => setTick((t) => t + 1) };
}

// useAuthorMap returns a lookup Map<id, AuthorEntry> built off the
// same /admin/authors fetch. Pages that render an author per row
// (ArticlesList, ReviewQueue, ArticleReview activity log) read from
// the map instead of the hardcoded AUTHORS mock — that mock only
// covers the five seed personas (clarissa, marcus, …) and any real
// auth_users UUID would crash with `undefined.initials`.
//
// Falls back to a synthetic "Unknown author" entry so callers don't
// need null-checks; the page renders gracefully even if the article
// references a deleted or never-fetched author id.
export function useAuthorMap(): {
  byId: Map<string, AuthorEntry>;
  resolve: (id: string | undefined | null) => AuthorEntry;
  loading: boolean;
} {
  const { authors, loading } = useAuthors();
  return useMemo(() => {
    const byId = new Map<string, AuthorEntry>();
    for (const a of authors) byId.set(a.id, a);
    const resolve = (id: string | undefined | null): AuthorEntry => {
      if (id && byId.has(id)) return byId.get(id)!;
      return UNKNOWN_AUTHOR;
    };
    return { byId, resolve, loading };
  }, [authors, loading]);
}

const UNKNOWN_AUTHOR: AuthorEntry = {
  id: "",
  wire: {
    id: "",
    email: "",
    name: "Unknown author",
    agency: "",
    cea_number: "",
    editorial_role: "contributor",
    status: "active",
    published: 0,
    drafts: 0,
    in_review: 0,
    total_leads: 0,
  },
  display: { name: "Unknown author", initials: "?", tint: "#6e7974", team: "—" },
  stats: { drafts: 0, awaiting: 0, published: 0, leads: 0, views: 0, last: "—" },
};

export function useAuthorActions() {
  return useMemo(
    () => ({
      enabled: articlesApiEnabled(),
      async setRole(id: string, role: EditorialRole): Promise<void> {
        if (!articlesApiEnabled()) return;
        await api.patch(`/admin/authors/${encodeURIComponent(id)}`, { editorial_role: role });
      },
    }),
    [],
  );
}
